import { Divider, Theme, useMediaQuery } from "@material-ui/core";
import { ReduxState, setSidebarVisibility } from "ra-core";
import React, { FC, useState, useEffect } from "react";
import { MenuItemLink, useDataProvider } from "react-admin";
import { useSelector, useDispatch } from "react-redux";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import SettingsIcon from "@material-ui/icons/Settings";
import DashIcon from "@material-ui/icons/Dashboard";

import OperationIcon from "@material-ui/icons/AssignmentInd";
import ServiceIcon from "@material-ui/icons/RoomService";
import FinanceIcon from "@material-ui/icons/LocalAtm";
import CmsIcon from "@material-ui/icons/BusinessCenter";
import RoutePlannerIcon from "@material-ui/icons/Explore";
import { makeStyles } from "@material-ui/core/styles";
import DescriptionIcon from "@material-ui/icons/Description";
import MoneyOffIcon from "@material-ui/icons/MoneyOff";

import school from "pages/schools";
import student from "pages/students";
import parent from "pages/parents";
import subcontractor from "pages/subcontractors";
import vehicles from "pages/vehicles";
import drivers from "pages/drivers";
import busAssistants from "pages/busAssistants";
import assignments from "pages/assignments";
import notTakingStudents from "pages/notTakingStudents";
import changeRequestForms from "pages/changeRequestForms";
import notTakings from "pages/notTakings";
import companies from "pages/companies";
import personInCharges from "pages/personInCharges";
import GeneralLedger from "pages/generalLedger";
import CreditNotes from "pages/creditNotes";
import BusSchedules from "pages/busSchedules";
import customerReceipts from "pages/customerReceipts";
import emailLogs from "../../pages/emailLogs";
import notifications from "pages/notifications";
import dueRenewalStudents from "pages/dueRenewalStudents";

import { find } from "lodash";

import {
  changeActiveNav,
  changeActiveSchool,
  rehydrateNavData,
} from "redux/navmode.action";

import * as MenuConst from "./menu.constants";

import SubMenu from "./SubMenu";

import "./menu.scss";

type SubMenuName = "masterData";

export type ThemeName = "light" | "dark";
interface AppState extends ReduxState {
  theme: ThemeName;
}

interface Props {
  dense: boolean;
  logout: () => void;
  onMenuClick: () => void;
}

const useStyles = makeStyles({
  selectSchoolAndRole: {
    "& .MuiSelect-root": {
      width: "100%",
    },
    "& .MuiSelect-select": {
      marginTop: "10px",
      color: "#5f5f5f",
      display: "flex",
      alignItems: "center",
      marginLeft: "5%",
      marginRight: "5%",
      padding: "13px !important",
      borderRadius: "10px !important",
      backgroundColor: "#DEEAFF",
    },
    "& .MuiSelect-select:focus": {
      backgroundColor: "#DEEAFF",
      color: "#5f5f5f",
    },
    "& .MuiSelect-icon": {
      display: "none !important",
    },
  },

  divider: {
    margin: "10px 15px 0 15px",
  },
});

const Menu: FC<Props> = ({ onMenuClick, dense, logout }) => {
  const classes = useStyles();
  const provider = useDataProvider();
  const [schools, setSchools] = useState([]);

  // const allSchool: any = {
  //   id: 0,
  //   name: "All Schools",
  // };

  const roles: string[] = localStorage.getItem("roles")
    ? localStorage.getItem("roles").split(",")
    : [];

  const router = useSelector((state: AppState) => state.router);

  const adminModeSelection = [
    {
      key: "CMS_ADMIN",
      icon: <CmsIcon />,
      text: "CMS Admin",
    },
    {
      key: "OPERATION",
      icon: <OperationIcon />,
      text: "Operation",
    },
    {
      key: "CUSTOMER_SERVICE",
      icon: <ServiceIcon />,
      text: "Customer Service",
    },
    {
      key: "FINANCE",
      icon: <FinanceIcon />,
      text: "Finance",
    },
    {
      key: "ROUTE_PLANNER",
      icon: <RoutePlannerIcon />,
      text: "Route Planner",
    },
  ];

  const financeModeSelection = [
    {
      key: "FINANCE",
      icon: <FinanceIcon />,
      text: "Finance",
    },
  ];

  const operationModeSelection = [
    {
      key: "OPERATION",
      icon: <OperationIcon />,
      text: "Operation",
    },
  ];

  const routeplannerModeSelection = [
    {
      key: "ROUTE_PLANNER",
      icon: <RoutePlannerIcon />,
      text: "Route Planner",
    },
  ];

  const customerServiceModeSelection = [
    {
      key: "CUSTOMER_SERVICE",
      icon: <ServiceIcon />,
      text: "Customer Service",
    },
  ];

  const getModeSelection = (roles: string[]) => {
    let modeSelection: any[] = [];

    if (roles.includes("superadmin")) {
      modeSelection = [...modeSelection, ...adminModeSelection];
    }

    if (roles.includes("admin")) {
      modeSelection = [...modeSelection, ...adminModeSelection];
    }

    if (roles.includes("finance")) {
      modeSelection = [...modeSelection, ...financeModeSelection];
    }

    if (roles.includes("operation")) {
      modeSelection = [
        ...modeSelection,
        ...operationModeSelection,
        ...routeplannerModeSelection,
      ];
    }

    if (roles.includes("school")) {
      modeSelection = [
        ...modeSelection,
        ...customerServiceModeSelection,
        ...operationModeSelection,
      ];
    }

    if (roles.includes("customerservice")) {
      modeSelection = [...modeSelection, ...customerServiceModeSelection];
    }

    return modeSelection;
  };

  const modeSelection = getModeSelection(roles);

  useEffect(() => {
    getSchools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router]);

  const getSchools = () => {
    const rolesFiltered = [
      "admin",
      "operation",
      "customerservice",
      "finance",
      "school",
    ];
    let filter = {};

    if (roles.some((role) => rolesFiltered.includes(role))) {
      filter = { UserId: Number(localStorage.getItem("userId")) };
    }

    provider
      .getList("schools", {
        filter: filter,
        pagination: {
          page: 1,
          perPage: 1000,
        },
        sort: {
          field: "Id",
          order: "ASC",
        },
      })
      .then((result: any) => {
        setSchools(result.data);
      })
      .catch(() => {
        setSchools([]);
      });
  };

  const dispatch = useDispatch();

  const [subMenus, setSubmenus] = useState({
    masterData: false,
  });

  // const translate = useTranslate();
  const isXSmall = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("xs")
  );
  const open = useSelector((state: AppState) => state.admin.ui.sidebarOpen);
  useSelector((state: AppState) => state.theme); // force rerender on theme change

  const navMode = useSelector((state: AppState) => state.navMode);

  useSelector((state: AppState) => {
    console.log("STATE", state);
  });

  useEffect(() => {
    dispatch(rehydrateNavData());
    if (navMode.school === null) {
      // if (!localStorage.getItem("roles").includes("school")) {
      //   // dispatch(changeActiveSchool(allSchool));
      // } else {
      if (schools.length > 0) {
        dispatch(changeActiveSchool(schools[0]));
      }
      // }
    }

    if (navMode.mode === null) {
      dispatch(changeActiveNav(MenuConst.CMS_ADMIN));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schools]);

  // useEffect(() => {
  //   if (navMode.school?.id === 0) {
  //     dispatch(changeActiveSchool(schools[0]));
  //   }
  // }, [navMode.mode]);

  useEffect(() => {
    const splittedValue = router.location.pathname.split("/");

    if (splittedValue[1] === "busSchedulePlans" && splittedValue.length >= 3) {
      dispatch(setSidebarVisibility(false));
    }

    // if (splittedValue[1] === "customerReceipts" && navMode.school?.id === 0) {
    //   dispatch(changeActiveSchool(schools[0]));
    // }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router]);

  useEffect(() => {
    switch (roles[0]) {
      // case "superadmin":
      //   dispatch(changeActiveNav(MenuConst.CMS_ADMIN));
      //   break;
      case "finance":
        dispatch(changeActiveNav(MenuConst.FINANCE));
        break;
      case "operation":
        switch (localStorage.getItem("mode")) {
          case "OPERATION":
            dispatch(changeActiveNav(MenuConst.OPERATION));
            break;
          case "ROUTE_PLANNER":
            dispatch(changeActiveNav(MenuConst.ROUTE_PLANNER));
            break;
          default:
            dispatch(changeActiveNav(MenuConst.OPERATION));
            break;
        }

        break;
      case "customerservice":
        dispatch(changeActiveNav(MenuConst.CUSTOMER_SERVICE));
        break;

      case "school":
        switch (localStorage.getItem("mode")) {
          case "CUSTOMER_SERVICE":
            dispatch(changeActiveNav(MenuConst.CUSTOMER_SERVICE));
            break;
          case "OPERATION":
            dispatch(changeActiveNav(MenuConst.OPERATION));
            break;
          default:
            dispatch(changeActiveNav(MenuConst.CUSTOMER_SERVICE));
            break;
        }
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStorage.getItem("roles")]);

  const handleToggleSubMenu = (toggledSubMenu: SubMenuName) => {
    setSubmenus({ ...subMenus, [toggledSubMenu]: !subMenus[toggledSubMenu] });
  };

  const onSelectNav = (event: any) => {
    const { value } = event.target;

    dispatch(changeActiveNav(value));
  };

  const onSelectSchool = (event: any) => {
    const { value } = event.target;

    // if (value === 0) {
    //   dispatch(changeActiveSchool(allSchool));
    // } else {
    dispatch(changeActiveSchool(find(schools, ["id", value])));
    // }
  };

  const renderMenuWithoutSchool = (mode: string, roles: string[]) => {
    switch (mode) {
      case MenuConst.CUSTOMER_SERVICE:
        return (
          <>
            {roles.includes("school") ? (
              <></>
            ) : (
              <>
                <Divider className={classes.divider} />
                <MenuItemLink
                  to={`/companies`}
                  primaryText={"Companies"}
                  leftIcon={<companies.icon />}
                  onClick={onMenuClick}
                  sidebarIsOpen={open}
                  dense={dense}
                />
                <MenuItemLink
                  to={`/personInCharges`}
                  primaryText={"Person In Charges"}
                  leftIcon={<personInCharges.icon />}
                  onClick={onMenuClick}
                  sidebarIsOpen={open}
                  dense={dense}
                />
                {/*<MenuItemLink*/}
                {/*  to={`/personInChargesWithoutStudent`}*/}
                {/*  primaryText={"Person In Charges Without Student"}*/}
                {/*  leftIcon={<personInCharges.icon />}*/}
                {/*  onClick={onMenuClick}*/}
                {/*  sidebarIsOpen={open}*/}
                {/*  dense={dense}*/}
                {/*  style={{ whiteSpace: "normal" }}*/}
                {/*/>*/}
                <MenuItemLink
                  to={`/parentsWithoutStudent`}
                  primaryText={"Parents Without Student"}
                  leftIcon={<parent.icon />}
                  onClick={onMenuClick}
                  sidebarIsOpen={open}
                  dense={dense}
                  style={{ whiteSpace: "normal" }}
                />
              </>
            )}
          </>
        );

      case MenuConst.FINANCE:
        return (
          <>
            {roles.includes("school") ? (
              <></>
            ) : (
              <>
                <Divider className={classes.divider} />
                <MenuItemLink
                  to={`/ledgers`}
                  primaryText={"General Ledger"}
                  leftIcon={<GeneralLedger.icon />}
                  onClick={onMenuClick}
                  sidebarIsOpen={open}
                  dense={dense}
                />
                <MenuItemLink
                  to={`/creditNotes`}
                  primaryText={"Credit Notes"}
                  leftIcon={<CreditNotes.icon />}
                  onClick={onMenuClick}
                  sidebarIsOpen={open}
                  dense={dense}
                />
                <MenuItemLink
                  to={`/invoices`}
                  primaryText={"Invoices"}
                  leftIcon={<FinanceIcon />}
                  onClick={onMenuClick}
                  sidebarIsOpen={open}
                  dense={dense}
                />
                <MenuItemLink
                  to={`/companies`}
                  primaryText={"Companies"}
                  leftIcon={<companies.icon />}
                  onClick={onMenuClick}
                  sidebarIsOpen={open}
                  dense={dense}
                />
                <MenuItemLink
                  to={`/personInCharges`}
                  primaryText={"Person In Charges"}
                  leftIcon={<personInCharges.icon />}
                  onClick={onMenuClick}
                  sidebarIsOpen={open}
                  dense={dense}
                />
              </>
            )}
          </>
        );

      case MenuConst.OPERATION:
        return (
          <>
            {roles.includes("school") ? (
              <></>
            ) : (
              <>
                <Divider className={classes.divider} />
                <MenuItemLink
                  to={`/notifications`}
                  primaryText={"Notifications"}
                  leftIcon={<notifications.icon />}
                  onClick={onMenuClick}
                  sidebarIsOpen={open}
                  dense={dense}
                />
                <MenuItemLink
                  to={`/subcontractors`}
                  primaryText={"Subcontractors"}
                  leftIcon={<subcontractor.icon />}
                  onClick={onMenuClick}
                  sidebarIsOpen={open}
                  dense={dense}
                />
                <MenuItemLink
                  to={`/drivers`}
                  primaryText={"Drivers"}
                  leftIcon={<drivers.icon />}
                  onClick={onMenuClick}
                  sidebarIsOpen={open}
                  dense={dense}
                />
                <MenuItemLink
                  to={`/busAssistants`}
                  primaryText={"Bus Assistants"}
                  leftIcon={<busAssistants.icon />}
                  onClick={onMenuClick}
                  sidebarIsOpen={open}
                  dense={dense}
                />
                <MenuItemLink
                  to={`/vehicles`}
                  primaryText={"Vehicles"}
                  leftIcon={<vehicles.icon />}
                  onClick={onMenuClick}
                  sidebarIsOpen={open}
                  dense={dense}
                />
              </>
            )}
          </>
        );
    }
  };

  const renderMenu = (mode: string, roles: string[]) => {
    switch (mode) {
      case MenuConst.OPERATION:
        return (
          <>
            {roles.includes("school") ? (
              <MenuItemLink
                to={`/assignments`}
                primaryText={"Bus Services"}
                leftIcon={<assignments.icon />}
                onClick={onMenuClick}
                sidebarIsOpen={open}
                dense={dense}
              />
            ) : (
              <>
                <MenuItemLink
                  to={`/assignments`}
                  primaryText={"Bus Services"}
                  leftIcon={<assignments.icon />}
                  onClick={onMenuClick}
                  sidebarIsOpen={open}
                  dense={dense}
                />
                <MenuItemLink
                  to={`/notTakings`}
                  primaryText={"Not Takings"}
                  leftIcon={<notTakings.icon />}
                  onClick={onMenuClick}
                  sidebarIsOpen={open}
                  dense={dense}
                />
              </>
            )}
          </>
        );
      case MenuConst.CMS_ADMIN:
        return (
          <>
            <MenuItemLink
              to={`/schools`}
              primaryText={"Schools"}
              leftIcon={<school.icon />}
              onClick={onMenuClick}
              sidebarIsOpen={open}
              dense={dense}
            />
            <MenuItemLink
              to={`/unapprovedInvoices`}
              primaryText={"Unapproved Invoices"}
              leftIcon={<DescriptionIcon />}
              onClick={onMenuClick}
              sidebarIsOpen={open}
              dense={dense}
            />
            <MenuItemLink
              to={`/unapprovedCreditNotes`}
              primaryText={"Unapproved Credit Notes"}
              leftIcon={<MoneyOffIcon />}
              onClick={onMenuClick}
              sidebarIsOpen={open}
              dense={dense}
              style={{ whiteSpace: "normal" }}
            />
            <MenuItemLink
              to={`/emailSendingLogs`}
              primaryText={"Email Logs"}
              leftIcon={<emailLogs.icon />}
              onClick={onMenuClick}
              sidebarIsOpen={open}
              dense={dense}
            />

            {roles.includes("superadmin") && (
              <SubMenu
                handleToggle={() => handleToggleSubMenu("masterData")}
                isOpen={subMenus.masterData}
                sidebarIsOpen={open}
                name="Master Data"
                icon={<SettingsIcon />}
                dense={dense}
              >
                <MenuItemLink
                  to={`/users`}
                  primaryText={"Users"}
                  leftIcon={<SettingsIcon />}
                  onClick={onMenuClick}
                  sidebarIsOpen={open}
                  dense={dense}
                />
              </SubMenu>
            )}
          </>
        );
      case MenuConst.CUSTOMER_SERVICE:
        return (
          <>
            {roles.includes("school") ? (
              <>
                <MenuItemLink
                  to={`/students`}
                  primaryText={"Students"}
                  leftIcon={<student.icon />}
                  onClick={onMenuClick}
                  sidebarIsOpen={open}
                  dense={dense}
                />
                <MenuItemLink
                  to={`/parents`}
                  primaryText={"Parents"}
                  leftIcon={<parent.icon />}
                  onClick={onMenuClick}
                  sidebarIsOpen={open}
                  dense={dense}
                />
              </>
            ) : (
              <>
                <MenuItemLink
                  to={`/students`}
                  primaryText={"Students"}
                  leftIcon={<student.icon />}
                  onClick={onMenuClick}
                  sidebarIsOpen={open}
                  dense={dense}
                />
                <MenuItemLink
                  to={`/parents`}
                  primaryText={"Parents"}
                  leftIcon={<parent.icon />}
                  onClick={onMenuClick}
                  sidebarIsOpen={open}
                  dense={dense}
                />
                <MenuItemLink
                  to={`/changeRequestForms`}
                  primaryText={"Change Request"}
                  leftIcon={<changeRequestForms.icon />}
                  onClick={onMenuClick}
                  sidebarIsOpen={open}
                  dense={dense}
                />
                <MenuItemLink
                  to={`/studentRenewals`}
                  primaryText={"Due For Renewal Students"}
                  leftIcon={<dueRenewalStudents.icon />}
                  onClick={onMenuClick}
                  sidebarIsOpen={open}
                  dense={dense}
                  style={{ whiteSpace: "normal" }}
                />
              </>
            )}
          </>
        );

      case MenuConst.FINANCE:
        return (
          <>
            <MenuItemLink
              to={`/students`}
              primaryText={"Students"}
              leftIcon={<student.icon />}
              onClick={onMenuClick}
              sidebarIsOpen={open}
              dense={dense}
            />
            <MenuItemLink
              to={`/parents`}
              primaryText={"Parents"}
              leftIcon={<parent.icon />}
              onClick={onMenuClick}
              sidebarIsOpen={open}
              dense={dense}
            />
            <MenuItemLink
              to={`/customerReceipts`}
              primaryText={"Receipts"}
              leftIcon={<customerReceipts.icon />}
              onClick={onMenuClick}
              sidebarIsOpen={open}
              dense={dense}
            />
          </>
        );

      case MenuConst.ROUTE_PLANNER:
        return (
          <>
            <MenuItemLink
              to={`/busSchedulePlans`}
              primaryText={"Route Plans"}
              leftIcon={<RoutePlannerIcon />}
              onClick={onMenuClick}
              sidebarIsOpen={open}
              dense={dense}
            />
            <MenuItemLink
              to={`/notTakingStudents`}
              primaryText={"Exclude Students"}
              leftIcon={<notTakingStudents.icon />}
              onClick={onMenuClick}
              sidebarIsOpen={open}
              dense={dense}
            />
            <MenuItemLink
              to={`/changeRequestForms`}
              primaryText={"Change Request"}
              leftIcon={<changeRequestForms.icon />}
              onClick={onMenuClick}
              sidebarIsOpen={open}
              dense={dense}
            />
          </>
        );

      default:
        break;
    }
  };

  return (
    <div>
      <Select
        value={navMode.mode}
        className={classes.selectSchoolAndRole}
        onChange={onSelectNav}
      >
        {modeSelection?.map((item: any, key: number) => {
          return (
            <MenuItem value={item.key} key={`selection-${key}`}>
              {item.icon}
              <div className="SelectionRoleText">{item.text}</div>
            </MenuItem>
          );
        })}
      </Select>

      {navMode.school !== null && navMode.mode !== MenuConst.CMS_ADMIN && (
        <Select
          className={classes.selectSchoolAndRole}
          value={navMode.school?.id}
          onChange={onSelectSchool}
        >
          {/*{schools.length > 1 &&*/}
          {/*  !router.location.pathname.includes("/customerReceipts") && (*/}
          {/*    <MenuItem value={0} key={`school-0`}>*/}
          {/*      <span className="SelectionTextAllSchools">All Schools</span>*/}
          {/*      <ArrowDropDownIcon />*/}
          {/*    </MenuItem>*/}
          {/*  )}*/}

          {schools.map((item: ISchool, key: number) => {
            return (
              <MenuItem value={item.id} key={`school-${key}`}>
                <img
                  className="SchoolImage"
                  src={
                    item.photoUrl === null
                      ? "https://upload.wikimedia.org/wikipedia/commons/thumb/a/ac/No_image_available.svg/600px-No_image_available.svg.png"
                      : item.photoUrl
                  }
                  alt="school-img"
                />
                <div className="SelectionText">{item.name}</div>
              </MenuItem>
            );
          })}
        </Select>
      )}

      <MenuItemLink
        to={`/dashboard`}
        primaryText={"Dashboard"}
        leftIcon={<DashIcon />}
        onClick={onMenuClick}
        sidebarIsOpen={open}
        dense={dense}
      />

      {renderMenu(navMode.mode, roles)}

      {renderMenuWithoutSchool(navMode.mode, roles)}

      {isXSmall && logout}
    </div>
  );
};

export default Menu;
